import { addDays, startOfCalendarWeek } from '~/calculations/calendar'
import type { DailyCheckPatch, DailyCheckRecord, TernaryAnswer } from '~/models/dailyCheck'
import { tally } from '~/models/dailyCheck'
import {
  dailyQuestionKindFor,
  hasProteinTarget,
  type NutritionPlanRecord,
  requiresDailyCheck,
} from '~/models/nutritionPlan'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import {
  getDailyCheck,
  listDailyChecksInRange,
  putDailyCheck,
} from '~/storage/repositories/dailyCheckRepository'
import { getCurrentNutritionPlan } from '~/storage/repositories/nutritionPlanRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { listWeeklyReviewsForWeek } from '~/storage/repositories/weeklyReviewRepository'
import { type CalendarDate, calendarDateFrom, unwrapDate } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { getEffectiveWeekStartDay } from './weekBoundaryService'

/**
 * The daily three taps, and the boundary that closes them (D-096).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE CORRECTION BOUNDARY LIVES HERE, NOT IN THE REPOSITORY
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `putDailyCheck` deliberately accepts any day, and the comment there says why:
 * whether a day may still be corrected depends on the weekly review covering it,
 * which the repository cannot see without reaching into another store — and a
 * repository that read a review to decide whether to accept a write would put a
 * product rule in the wrong layer.
 *
 * So this service owns it. **A day is correctable until the review covering its
 * week is finalized, and read-only afterwards**, because D-046 forbids
 * retroactively changing a week the user has already been answered about. The
 * refusal is explicit and carries the reason, so the interface can state the
 * boundary rather than appearing to accept a tap and silently discarding it.
 */

/** Why a day could not be recorded. Distinct so the screen can explain it. */
export interface DayClosedError {
  readonly kind: 'dayClosed'
  readonly message: string
  readonly weekStartDate: CalendarDate
}

export type DailyCheckError = DayClosedError | StorageError

export function isDayClosed(error: DailyCheckError): error is DayClosedError {
  return error.kind === 'dayClosed'
}

/**
 * Whether the week covering this day has been answered already.
 *
 * Pure, so the boundary can be tested without a database and so the screen can
 * ask the same question the write path asks. A week with a draft review is still
 * open: the user has not been given an opinion about it yet, and a draft is
 * precisely the state in which they are still filling the week in.
 */
export function isWeekFinalized(
  reviews: readonly { readonly status: string }[] | undefined,
): boolean {
  return reviews?.some((review) => review.status === 'complete') ?? false
}

/**
 * What a closed week says, wherever it is said.
 *
 * Training session outcomes take **the same boundary, the same error type and
 * the same visible sentence** as a nutrition day (D-105 §11), so the sentence
 * lives here once rather than being restated beside the second caller. Two copies
 * would drift, and the user would meet two different explanations of one rule.
 */
export const WEEK_CLOSED_MESSAGE =
  'This week has been reviewed, so its days can no longer be changed. Project Sola will not alter a week it has already answered.'

/**
 * Record or correct a day's answers, refusing a day whose week is closed.
 *
 * The check happens before the write rather than after, so a refused correction
 * leaves the stored day exactly as the finalized review saw it.
 */
export async function recordDailyCheck(
  db: SolaDatabase,
  date: CalendarDate,
  patch: DailyCheckPatch,
  now: Date,
): Promise<Result<DailyCheckRecord, DailyCheckError>> {
  const weekStartDay = await getEffectiveWeekStartDay(db, now)
  if (!weekStartDay.ok) return weekStartDay

  const weekStart = startOfCalendarWeek(date, weekStartDay.value)
  const reviews = await listWeeklyReviewsForWeek(db, weekStart)
  if (!reviews.ok) return reviews

  if (isWeekFinalized(reviews.value)) {
    return err({ kind: 'dayClosed', message: WEEK_CLOSED_MESSAGE, weekStartDate: weekStart })
  }

  return putDailyCheck(db, unwrapDate(date), patch, now)
}

/* ── What Today asks ─────────────────────────────────────────────────────── */

/** One question on the daily surface. */
export interface DailyQuestion {
  readonly field: 'calorieTarget' | 'proteinTarget' | 'followedPlan'
  readonly label: string
  readonly answer: TernaryAnswer | undefined
}

/**
 * What the daily nutrition surface should show, or nothing at all.
 *
 * **`undefined` where there is nothing to ask**, rather than an empty prompt:
 * D-096 is explicit that a user with no nutrition target gets *nothing* — no
 * empty task and no prompt. A task that cannot be completed is worse than no
 * task, and it is the shape that trains someone to ignore the screen.
 */
export interface DailyNutritionPrompt {
  readonly date: CalendarDate
  readonly questions: readonly DailyQuestion[]
  /** What the user calls the authority, for the screen only — never for a rule. */
  readonly providerLabel: string | undefined
  /** True once the covering week has been reviewed. The screen says so. */
  readonly closed: boolean
}

/**
 * The plan in force for whatever project is running, or nothing.
 *
 * One helper rather than the project-then-plan pair written out at each caller:
 * the two reads always go together, and a caller that forgot the project lookup
 * would silently read another project's targets.
 */
export async function getActiveNutritionPlan(
  db: SolaDatabase,
): Promise<Result<NutritionPlanRecord | undefined, StorageError>> {
  const project = await getActiveProject(db)
  if (!project.ok) return project
  if (project.value === undefined) return ok(undefined)

  return getCurrentNutritionPlan(db, project.value.id)
}

export async function getDailyNutritionPrompt(
  db: SolaDatabase,
  now: Date,
): Promise<Result<DailyNutritionPrompt | undefined, StorageError>> {
  const date = calendarDateFrom(now)

  const plan = await getActiveNutritionPlan(db)
  if (!plan.ok) return plan
  if (plan.value === undefined || !requiresDailyCheck(plan.value)) return ok(undefined)

  const today = await getDailyCheck(db, unwrapDate(date))
  if (!today.ok) return today

  const weekStartDay = await getEffectiveWeekStartDay(db, now)
  if (!weekStartDay.ok) return weekStartDay
  const reviews = await listWeeklyReviewsForWeek(db, startOfCalendarWeek(date, weekStartDay.value))
  if (!reviews.ok) return reviews

  return ok({
    date,
    questions: questionsFor(plan.value, today.value),
    providerLabel: plan.value.providerLabel,
    closed: isWeekFinalized(reviews.value),
  })
}

/**
 * The questions this plan asks, with whatever was already answered.
 *
 * A `nonNumeric` plan asks one question and the numeric ones ask up to two — and
 * a numeric plan with no protein figure does not ask about protein, because there
 * is no target to have reached. Pure, so the mapping is testable without storage.
 */
export function questionsFor(
  plan: NutritionPlanRecord,
  record: DailyCheckRecord | undefined,
): readonly DailyQuestion[] {
  const nutrition = record?.nutrition

  if (dailyQuestionKindFor(plan) === 'followedPlan') {
    return [
      {
        field: 'followedPlan',
        label: 'Followed your plan today?',
        answer: nutrition?.followedPlan,
      },
    ]
  }

  const questions: DailyQuestion[] = []
  if (plan.calorieTarget !== undefined) {
    questions.push({
      field: 'calorieTarget',
      label: 'Within your calorie target?',
      answer: nutrition?.calorieTarget,
    })
  }
  if (hasProteinTarget(plan)) {
    questions.push({
      field: 'proteinTarget',
      label: 'Reached your protein target?',
      answer: nutrition?.proteinTarget,
    })
  }
  return questions
}

/* ── The weekly summary ──────────────────────────────────────────────────── */

/**
 * The week's counts, for the review step to show.
 *
 * **Counts, never a percentage.** `tally` refuses to divide and this refuses to
 * make one: a week with two answered days out of seven has at most two met, and
 * reporting `2/7` would render five unanswered days as five failures. Days the
 * user did not answer, and days answered `unsure`, are carried separately so the
 * screen can say "you answered two days" without implying anything about the rest.
 */
export interface WeeklyNutritionSummary {
  readonly weekStartDate: CalendarDate
  readonly calorieTarget: ReturnType<typeof tally>
  readonly proteinTarget: ReturnType<typeof tally>
  readonly followedPlan: ReturnType<typeof tally>
}

const DAYS_IN_WEEK = 7

export async function summariseNutritionWeek(
  db: SolaDatabase,
  weekStartDate: CalendarDate,
): Promise<Result<WeeklyNutritionSummary, StorageError>> {
  const lastDay = addDays(weekStartDate, DAYS_IN_WEEK - 1)
  const days = await listDailyChecksInRange(db, unwrapDate(weekStartDate), unwrapDate(lastDay))
  if (!days.ok) return days

  return ok({
    weekStartDate,
    calorieTarget: tally(days.value, (record) => record.nutrition?.calorieTarget),
    proteinTarget: tally(days.value, (record) => record.nutrition?.proteinTarget),
    followedPlan: tally(days.value, (record) => record.nutrition?.followedPlan),
  })
}

export { storageError }
